import { FormEvent, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

import { AdminUserCreateResponse, AdminUserEditCompanyOption } from '../../services/api';
import { useAdminUserMutations } from '../../hooks/useApi';
import { getMutationErrorMessage } from '../../lib/apiErrors';
import { useTouchedFields } from '../../lib/touchedFormValidation';
import { cn, pluralize } from '../../lib/utils';
import { Button } from '../ui/button';
import { dialogFooterClassName } from '../ui/dialog-frame';
import { Field, FormErrorAlert, useFocusFirstInvalid } from '../ui/form-field';
import { useModalDialog } from '../ui/use-modal-dialog';
import {
    formCheckboxListSurfaceClassName,
    formFieldGroupControlClassName,
    formFieldGroupSurfaceClassName,
} from '../ui/formPrimitives';
import { Input } from '../ui/input';
import { ModalOverlay, modalSheetClassName } from '../ui/modal-overlay';
import { dialogTitleClassName } from '../ui/typography';
import { CompanyCombobox } from './CompanyCombobox';
import { formatCompanyLabel, formatTeamLabel } from './companyComboboxLabels';

interface AdminCreateUserModalProps {
    open: boolean;
    onClose: () => void;
    onCreated?: (result: AdminUserCreateResponse) => void;
}

type CreateUserMode = 'single' | 'batch';
type BatchInputMode = 'emails' | 'csv';
type BatchUserStatus = 'idle' | 'creating' | 'success' | 'error';
type CreateUserFieldName = 'firstName' | 'lastName' | 'email' | 'company' | 'teams';

const CREATE_USER_FIELDS: readonly CreateUserFieldName[] = [
    'firstName',
    'lastName',
    'email',
    'company',
    'teams',
];

function getCreateUserFieldErrors({
    firstName,
    lastName,
    email,
    company,
    teamIds,
}: {
    firstName: string;
    lastName: string;
    email: string;
    company: AdminUserEditCompanyOption | null;
    teamIds: number[];
}): Partial<Record<CreateUserFieldName, string>> {
    return {
        firstName: !firstName.trim() ? 'First name is required.' : undefined,
        lastName: !lastName.trim() ? 'Last name is required.' : undefined,
        email: !email.trim() ? 'Email is required.' : undefined,
        company: !company ? 'Choose a company.' : undefined,
        teams: company && teamIds.length === 0 ? 'Choose at least one team.' : undefined,
    };
}

const BATCH_STATUS_LABELS: Record<Exclude<BatchUserStatus, 'error'>, string> = {
    idle: 'Ready',
    creating: 'Creating…',
    success: 'Created',
};

interface BatchUserDraft {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    status: BatchUserStatus;
    error?: string;
}

function toTitleCase(value: string): string {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function stripCsvQuotes(value: string): string {
    const trimmed = value.trim();
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        return trimmed.slice(1, -1).trim();
    }
    return trimmed;
}

function inferNamesFromEmail(email: string): { firstName: string; lastName: string } {
    const [localPart] = email.split('@');
    const parts = localPart.split(/[._-]+/).filter(Boolean);
    return {
        firstName: toTitleCase(parts[0] ?? ''),
        lastName: parts.length > 1 ? parts.slice(1).map(toTitleCase).join(' ') : '',
    };
}

function parseBatchUsers(input: string, mode: BatchInputMode): BatchUserDraft[] {
    const lines =
        mode === 'emails'
            ? input.split(/[\n,;]+/)
            : input.split(/\r?\n/).filter((line) => line.trim().length > 0);

    return lines
        .map((line, index) => {
            if (mode === 'emails') {
                const email = line.trim().toLowerCase();
                const names = inferNamesFromEmail(email);
                return {
                    id: `${index}-${email}`,
                    email,
                    ...names,
                    status: 'idle' as const,
                };
            }

            const [emailValue = '', firstName = '', lastName = ''] = line.split(',');
            return {
                id: `${index}-${emailValue.trim().toLowerCase()}`,
                email: stripCsvQuotes(emailValue).toLowerCase(),
                firstName: stripCsvQuotes(firstName),
                lastName: stripCsvQuotes(lastName),
                status: 'idle' as const,
            };
        })
        .filter((user) => user.email.length > 0);
}

export function AdminCreateUserModal({ open, onClose, onCreated }: AdminCreateUserModalProps) {
    const { createUser } = useAdminUserMutations();
    const [mode, setMode] = useState<CreateUserMode>('single');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [batchInputMode, setBatchInputMode] = useState<BatchInputMode>('emails');
    const [batchInput, setBatchInput] = useState('');
    const [batchUsers, setBatchUsers] = useState<BatchUserDraft[]>([]);
    const [isBatchCreating, setIsBatchCreating] = useState(false);
    const [company, setCompany] = useState<AdminUserEditCompanyOption | null>(null);
    const [teamIds, setTeamIds] = useState<number[]>([]);
    const [isActive, setIsActive] = useState(true);
    const [inviteUser, setInviteUser] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const isSubmitting = createUser.isPending || isBatchCreating;
    const formRef = useRef<HTMLFormElement>(null);
    const { touch, touchAll, resetTouched, fieldError } = useTouchedFields<CreateUserFieldName>();
    const focusFirstInvalid = useFocusFirstInvalid(formRef);
    const fieldErrors = getCreateUserFieldErrors({
        firstName,
        lastName,
        email,
        company,
        teamIds,
    });

    useEffect(() => {
        if (!open) return;
        setMode('single');
        setFirstName('');
        setLastName('');
        setEmail('');
        setBatchInputMode('emails');
        setBatchInput('');
        setBatchUsers([]);
        setIsBatchCreating(false);
        setCompany(null);
        setTeamIds([]);
        setIsActive(true);
        setInviteUser(true);
        setError(null);
        resetTouched();
    }, [open, resetTouched]);

    const { titleId, panelProps } = useModalDialog({ open, onClose });
    if (!open) return null;

    const toggleTeam = (teamId: number) => {
        setTeamIds((current) =>
            current.includes(teamId)
                ? current.filter((candidate) => candidate !== teamId)
                : [...current, teamId],
        );
    };

    const updateBatchUser = (
        userId: string,
        field: keyof Pick<BatchUserDraft, 'email' | 'firstName' | 'lastName'>,
        value: string,
    ) => {
        setBatchUsers((current) =>
            current.map((user) =>
                user.id === userId
                    ? {
                          ...user,
                          [field]: field === 'email' ? value.trim().toLowerCase() : value,
                          status: 'idle',
                          error: undefined,
                      }
                    : user,
            ),
        );
    };

    const handleParseBatchUsers = () => {
        const parsedUsers = parseBatchUsers(batchInput, batchInputMode);
        setBatchUsers(parsedUsers);
        setError(parsedUsers.length === 0 ? 'Add at least one user to create.' : null);
    };

    const handleBatchSubmit = async () => {
        if (batchUsers.length === 0) {
            setError('Parse at least one user to create.');
            return;
        }
        if (fieldErrors.company || fieldErrors.teams) {
            focusFirstInvalid();
            return;
        }
        if (!company) return;

        const invalidUser = batchUsers.find(
            (user) => !user.email.trim() || !user.firstName.trim() || !user.lastName.trim(),
        );
        if (invalidUser) {
            setError('Every batch user needs an email, first name, and last name.');
            return;
        }

        setIsBatchCreating(true);
        let lastCreated: AdminUserCreateResponse | null = null;
        let failures = 0;

        for (const user of batchUsers) {
            setBatchUsers((current) =>
                current.map((candidate) =>
                    candidate.id === user.id
                        ? { ...candidate, status: 'creating', error: undefined }
                        : candidate,
                ),
            );

            try {
                lastCreated = await createUser.mutateAsync({
                    firstName: user.firstName.trim(),
                    lastName: user.lastName.trim(),
                    email: user.email.trim().toLowerCase(),
                    companyId: company.companyId,
                    teamIds,
                    isActive,
                    inviteUser,
                });
                setBatchUsers((current) =>
                    current.map((candidate) =>
                        candidate.id === user.id
                            ? { ...candidate, status: 'success', error: undefined }
                            : candidate,
                    ),
                );
            } catch (err) {
                failures += 1;
                const message = getMutationErrorMessage(err, 'Could not create this user.');
                setBatchUsers((current) =>
                    current.map((candidate) =>
                        candidate.id === user.id
                            ? { ...candidate, status: 'error', error: message }
                            : candidate,
                    ),
                );
            }
        }

        setIsBatchCreating(false);

        if (failures > 0) {
            setError(
                `Could not create ${failures} ${pluralize(failures, 'user', 'users')}. See the Status column for details.`,
            );
            return;
        }

        if (lastCreated) {
            onCreated?.(lastCreated);
        }
        onClose();
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);
        touchAll(CREATE_USER_FIELDS);

        if (mode === 'batch') {
            await handleBatchSubmit();
            return;
        }

        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();
        const trimmedEmail = email.trim().toLowerCase();

        if (Object.values(fieldErrors).some(Boolean)) {
            focusFirstInvalid();
            return;
        }
        if (!company) return;

        try {
            const result = await createUser.mutateAsync({
                firstName: trimmedFirstName,
                lastName: trimmedLastName,
                email: trimmedEmail,
                companyId: company.companyId,
                teamIds,
                isActive,
                inviteUser,
            });
            onCreated?.(result);
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, 'Could not create user. Try again.'));
        }
    };

    return (
        <ModalOverlay>
            <div
                {...panelProps}
                className={cn(modalSheetClassName, 'flex max-h-[90vh] w-full max-w-3xl flex-col')}
            >
                <div className="flex shrink-0 items-start justify-between gap-4 border-b p-5">
                    <div>
                        <h2 id={titleId} className={dialogTitleClassName}>
                            Create user
                        </h2>
                        <p className="mt-1 text-sm text-neutral-500">Creates the user in Auth0.</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close dialog"
                        className="-m-1 flex size-9 shrink-0 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                    >
                        <X className="size-5" aria-hidden />
                    </button>
                </div>

                <form
                    ref={formRef}
                    className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5"
                    onSubmit={handleSubmit}
                >
                    <div className="inline-flex rounded-lg border border-neutral-200 p-1">
                        <ModeButton
                            active={mode === 'single'}
                            label="Create one user"
                            onClick={() => {
                                setMode('single');
                                setError(null);
                            }}
                        />
                        <ModeButton
                            active={mode === 'batch'}
                            label="Create multiple users"
                            onClick={() => {
                                setMode('batch');
                                setError(null);
                            }}
                        />
                    </div>

                    {error ? <FormErrorAlert message={error} /> : null}

                    {mode === 'single' ? (
                        <>
                            <div className="grid gap-4 md:grid-cols-2">
                                <Field
                                    label="First name"
                                    required
                                    error={fieldError('firstName', fieldErrors.firstName)}
                                >
                                    <Input
                                        value={firstName}
                                        onChange={(event) => setFirstName(event.target.value)}
                                        onBlur={() => touch('firstName')}
                                        autoComplete="off"
                                    />
                                </Field>
                                <Field
                                    label="Last name"
                                    required
                                    error={fieldError('lastName', fieldErrors.lastName)}
                                >
                                    <Input
                                        value={lastName}
                                        onChange={(event) => setLastName(event.target.value)}
                                        onBlur={() => touch('lastName')}
                                        autoComplete="off"
                                    />
                                </Field>
                            </div>

                            <Field
                                label="Email"
                                required
                                error={fieldError('email', fieldErrors.email)}
                            >
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                    onBlur={() => touch('email')}
                                    autoComplete="off"
                                />
                            </Field>
                        </>
                    ) : (
                        <BatchUserEditor
                            input={batchInput}
                            inputMode={batchInputMode}
                            onInputChange={setBatchInput}
                            onInputModeChange={(nextMode) => {
                                setBatchInputMode(nextMode);
                                setBatchUsers([]);
                                setError(null);
                            }}
                            onParse={handleParseBatchUsers}
                            onUpdateUser={updateBatchUser}
                            users={batchUsers}
                        />
                    )}

                    <Toggle
                        label="Active"
                        checked={isActive}
                        onChange={setIsActive}
                        description="Inactive users cannot sign in until activated."
                    />

                    <Toggle
                        label="Invite user"
                        checked={inviteUser}
                        onChange={setInviteUser}
                        description="Send the admin invite email after creating the user."
                    />

                    <Field
                        label="Company"
                        required
                        error={fieldError('company', fieldErrors.company)}
                    >
                        <CompanyCombobox
                            ariaLabel="Company"
                            value={company ? formatCompanyLabel(company) : ''}
                            selectedCompanyId={company?.companyId ?? null}
                            onSelect={(nextCompany) => {
                                setCompany(nextCompany);
                                setTeamIds([]);
                            }}
                        />
                    </Field>

                    <Field
                        as="group"
                        label="Teams"
                        required
                        error={fieldError('teams', fieldErrors.teams)}
                    >
                        <div className={formCheckboxListSurfaceClassName}>
                            {!company ? (
                                <p className="text-sm text-neutral-500">Choose a company first.</p>
                            ) : (
                                <div className="grid gap-2 md:grid-cols-2">
                                    {company.teams.map((team) => (
                                        <label
                                            key={team.teamId}
                                            className={cn(
                                                'flex items-center gap-2 rounded-md p-2 text-sm',
                                                formFieldGroupControlClassName,
                                            )}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={teamIds.includes(team.teamId)}
                                                onChange={() => toggleTeam(team.teamId)}
                                            />
                                            <span>{formatTeamLabel(team)}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Field>

                    <div className={dialogFooterClassName}>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                            {mode === 'batch'
                                ? isBatchCreating
                                    ? 'Creating…'
                                    : `Create ${batchUsers.length > 0 ? `${batchUsers.length} ` : ''}${pluralize(
                                          batchUsers.length,
                                          'user',
                                          'users',
                                      )}`
                                : createUser.isPending
                                  ? 'Creating…'
                                  : 'Create user'}
                        </Button>
                    </div>
                </form>
            </div>
        </ModalOverlay>
    );
}

function ModeButton({
    active,
    label,
    onClick,
}: {
    active: boolean;
    label: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            aria-pressed={active}
            className={
                active
                    ? 'rounded-md bg-neutral-900 px-3 py-1.5 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                    : 'rounded-md px-3 py-1.5 text-sm font-medium text-neutral-600 hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            }
            onClick={onClick}
        >
            {label}
        </button>
    );
}

function BatchUserEditor({
    input,
    inputMode,
    onInputChange,
    onInputModeChange,
    onParse,
    onUpdateUser,
    users,
}: {
    input: string;
    inputMode: BatchInputMode;
    onInputChange: (value: string) => void;
    onInputModeChange: (mode: BatchInputMode) => void;
    onParse: () => void;
    onUpdateUser: (
        userId: string,
        field: keyof Pick<BatchUserDraft, 'email' | 'firstName' | 'lastName'>,
        value: string,
    ) => void;
    users: BatchUserDraft[];
}) {
    return (
        <div className="space-y-4">
            <fieldset className={`space-y-2 ${formFieldGroupSurfaceClassName}`}>
                <legend className="text-sm font-semibold text-neutral-950">
                    Batch input format
                </legend>
                <div className="flex flex-col gap-2 sm:flex-row">
                    <label className="flex items-center gap-2 text-sm text-neutral-700">
                        <input
                            type="radio"
                            name="batch-input-format"
                            checked={inputMode === 'emails'}
                            onChange={() => onInputModeChange('emails')}
                        />
                        Emails only
                    </label>
                    <label className="flex items-center gap-2 text-sm text-neutral-700">
                        <input
                            type="radio"
                            name="batch-input-format"
                            checked={inputMode === 'csv'}
                            onChange={() => onInputModeChange('csv')}
                        />
                        CSV: email, first, last
                    </label>
                </div>
            </fieldset>

            <Field label="Users to create" required>
                <textarea
                    aria-label="Users to create"
                    className="min-h-28 w-full rounded-md border border-admin-field-border admin-field-fill px-3 py-2 text-sm text-neutral-900"
                    value={input}
                    onChange={(event) => onInputChange(event.target.value)}
                    placeholder={
                        inputMode === 'emails'
                            ? 'jane.doe@example.com, sam.smith@example.com'
                            : 'jane@example.com, Jane, Doe'
                    }
                />
            </Field>

            <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-neutral-500">
                    Review and edit the parsed users before creating them.
                </p>
                <Button type="button" variant="outline" onClick={onParse}>
                    Parse users
                </Button>
            </div>

            {users.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-neutral-200">
                    <table className="w-full min-w-[640px] text-left text-sm">
                        <thead className="bg-neutral-50 text-sm text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2 font-medium">Email</th>
                                <th className="px-3 py-2 font-medium">First</th>
                                <th className="px-3 py-2 font-medium">Last</th>
                                <th className="px-3 py-2 font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user, index) => (
                                <tr key={user.id} className="border-t border-neutral-200">
                                    <td className="px-3 py-2">
                                        <Input
                                            type="email"
                                            aria-label={`Email, row ${index + 1}`}
                                            autoComplete="off"
                                            value={user.email}
                                            onChange={(event) =>
                                                onUpdateUser(user.id, 'email', event.target.value)
                                            }
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <Input
                                            aria-label={`First name, row ${index + 1}`}
                                            autoComplete="off"
                                            value={user.firstName}
                                            onChange={(event) =>
                                                onUpdateUser(
                                                    user.id,
                                                    'firstName',
                                                    event.target.value,
                                                )
                                            }
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <Input
                                            aria-label={`Last name, row ${index + 1}`}
                                            autoComplete="off"
                                            value={user.lastName}
                                            onChange={(event) =>
                                                onUpdateUser(
                                                    user.id,
                                                    'lastName',
                                                    event.target.value,
                                                )
                                            }
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-xs text-neutral-600">
                                        {user.status === 'error'
                                            ? user.error
                                            : BATCH_STATUS_LABELS[user.status]}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function Toggle({
    checked,
    description,
    label,
    onChange,
}: {
    checked: boolean;
    description: string;
    label: string;
    onChange: (checked: boolean) => void;
}) {
    return (
        <label className={`flex items-start gap-3 ${formFieldGroupSurfaceClassName}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                className="mt-1 size-4 rounded border-neutral-300"
            />
            <span>
                <span className="block text-sm font-medium text-neutral-950">{label}</span>
                <span className="block text-xs text-neutral-500">{description}</span>
            </span>
        </label>
    );
}
